<template>
  <div
    v-if="initialised"
    class="list-container"
    :class="{ 'is-compact-list': isCompact }"
  >
    <div class="top-controls">
      <!-- Row 1: [a] New button  [b] Search  [c] Ownership -->
      <div class="control-grid top-row">
        <div class="cell-create">
          <template v-if="canCreate && creatableModel && showAddControls">
            <template v-if="isWorkflowList">
              <DropdownButton
                label="New Workflow"
                icon="fa fa-plus"
                size="small"
                variant="primary"
                outlined
              >
                <router-link
                  :to="{
                    name: 'stockNew',
                    params: { appName, modelName, modelType },
                  }"
                  class="dd-item"
                >
                  <span class="icon">
                    <i class="fa fa-plus" />
                  </span>
                  New Workflow
                </router-link>
                <router-link :to="{ name: 'workflowImport' }" class="dd-item">
                  <span class="icon">
                    <i class="fa-brands fa-git-alt"></i>
                  </span>
                  Import from Git
                </router-link>
                <router-link
                  v-tooltip="
                    'Search & import RO-Crate from a workflow repository'
                  "
                  :to="{ name: 'workflowImportROCrate' }"
                  class="dd-item"
                >
                  <span class="icon">
                    <i class="fa fa-cube" />
                  </span>
                  Import from Workflow Repository
                </router-link>
              </DropdownButton>
            </template>
            <template v-else-if="isWorkflowRunList">
              <DropdownButton
                label="New Workflow Run"
                icon="fa fa-plus"
                size="small"
                variant="primary"
                outlined
              >
                <router-link
                  :to="{
                    name: 'stockNew',
                    params: { appName, modelName, modelType },
                  }"
                  class="dd-item"
                >
                  <span class="icon">
                    <i class="fa fa-plus" />
                  </span>
                  New Workflow Run
                </router-link>
                <router-link
                  :to="{ name: 'workflowRunImportGalaxyView' }"
                  class="dd-item"
                >
                  <span class="icon">
                    <i class="fa fa-plus" />
                  </span>
                  Import Galaxy Workflow Invocation
                </router-link>
              </DropdownButton>
            </template>
            <template v-else-if="isOntologyList">
              <DropdownButton
                v-if="canAddOntology"
                label="New Ontology"
                icon="fa fa-plus"
                size="small"
                variant="primary"
                outlined
              >
                <router-link
                  :to="{ name: 'olsBrowseOntologies' }"
                  class="dd-item"
                >
                  <span class="icon"><i class="fa fa-download" /></span>
                  Import from Ontology Lookup Service (OLS)
                </router-link>
                <router-link
                  :to="{
                    name: 'stockNew',
                    params: { appName, modelName, modelType },
                  }"
                  class="dd-item"
                >
                  <span class="icon"><i class="fa fa-plus" /></span>
                  New Ontology
                </router-link>
              </DropdownButton>
            </template>
            <template v-else-if="isOntologyTermList">
              <DropdownButton
                v-if="canAddOntologyTerm"
                label="New Ontology Term"
                icon="fa fa-plus"
                size="small"
                variant="primary"
                outlined
              >
                <router-link :to="{ name: 'olsTermSearch' }" class="dd-item">
                  <span class="icon"><i class="fa fa-search" /></span>
                  Search &amp; Import from OLS
                </router-link>
                <router-link
                  :to="{
                    name: 'stockNew',
                    params: { appName, modelName, modelType },
                  }"
                  class="dd-item"
                >
                  <span class="icon"><i class="fa fa-plus" /></span>
                  New Ontology Term
                </router-link>
              </DropdownButton>
              <router-link v-else :to="{ name: 'olsTermSearch' }">
                <BaseButton
                  size="small"
                  variant="primary"
                  :outlined="true"
                  :icon="['fa', 'fa-search']"
                  label="Search &amp; Import from OLS"
                />
              </router-link>
            </template>
            <template v-else-if="isCategoryList && canAddCategory">
              <router-link
                :to="{
                  name: 'stockNew',
                  params: { appName, modelName, modelType },
                }"
                class="button is-small is-primary is-outlined"
              >
                <span class="icon is-small mr-1">
                  <i class="fa fa-plus" />
                </span>
                <span>New Category</span>
              </router-link>
            </template>
            <template v-else-if="isTermList && canAddTerm">
              <router-link
                :to="{
                  name: 'stockNew',
                  params: { appName, modelName, modelType },
                }"
                class="button is-small is-primary is-outlined"
              >
                <span class="icon is-small mr-1">
                  <i class="fa fa-plus" />
                </span>
                <span>New Term</span>
              </router-link>
            </template>
            <CreateItemButton
              v-else
              class="create-control"
              :app-name="appName"
              :model-type="modelType"
              :model-name="modelName"
              :show-sections="false"
              :show-batch-import="isBatchImportable"
            >
              <template
                v-if="modelType !== 'total' && isBatchImportable"
                #more-dropdown-content="{ currentSubsectionLabel }"
              >
                <div class="dd-section-label">
                  <span class="icon">
                    <i class="fa-solid fa-file-excel"></i>
                  </span>
                  Batch create {{ currentSubsectionLabel }}
                </div>
                <a
                  v-if="isBatchImportable"
                  v-tooltip="
                    `Obtain a spreadsheet (excel) template prefilled
                    with columns information. Later upload the filled in
                    spreadsheet to <b>batch create</b> the items`
                  "
                  class="dd-item"
                  @click="isOpenBatchCreateGetTemplateModal = true"
                  @keyup.enter="isOpenBatchCreateGetTemplateModal = true"
                >
                  Download template
                </a>
                <a
                  v-if="isBatchImportable"
                  class="dd-item"
                  @click="
                    () => {
                      $router.push({
                        name: 'batchImport',
                        params: { appName: appName, modelName: modelName },
                      })
                    }
                  "
                  @keyup.enter="
                    () => {
                      $router.push({
                        name: 'batchImport',
                        params: { appName: appName, modelName: modelName },
                      })
                    }
                  "
                >
                  Create items from template
                </a>
              </template>
            </CreateItemButton>
          </template>
        </div>
        <div class="cell-search">
          <div v-if="showGlobalSearch || !isCompact" class="global-search">
            <div class="control">
              <DropdownButton
                icon="fa-solid fa-cog"
                size="small"
                :variant="searchVisibleOnly ? 'primary' : null"
                :outlined="!searchVisibleOnly"
                :show-arrow="false"
              >
                <div
                  v-tooltip="
                    `<p>Apply search to <code>visible columns</code> only</p>
                    <p>The information in hidden column will be <u>ignored</u></p>`
                  "
                  class="dd-item"
                  @click.stop
                >
                  <ToggleSwitch
                    v-model="searchVisibleOnly"
                    label="Search only visible columns"
                  />
                </div>
              </DropdownButton>
            </div>
            <div class="control is-expanded">
              <div
                class="search-input-wrap"
                @mouseover="hoveringSearch = true"
                @mouseleave="hoveringSearch = false"
              >
                <input
                  v-model="search"
                  class="input is-small"
                  :class="{ 'is-primary': hasSearchSet }"
                  type="text"
                  placeholder="Search"
                  @keyup.enter="doSearch"
                  @blur="
                    () => {
                      if (lastSearch !== search) doSearch()
                    }
                  "
                />
                <span
                  v-if="hasSearchSet"
                  v-tooltip="
                    'A global search filter is set.<br />Click to remove it.'
                  "
                  class="search-filter-badge"
                  @click="clearSearch"
                >
                  <i class="fa-solid fa-filter fa-xs has-text-primary" />
                  <i
                    v-if="hoveringSearch"
                    class="fa-solid fa-xmark fa-xs has-text-danger"
                  />
                </span>
              </div>
            </div>
            <div class="control">
              <BaseButton
                icon="fa-solid fa-search"
                size="small"
                variant="primary"
                outlined
                @click="loadData"
              />
            </div>
          </div>
        </div>
        <div class="cell-ownership">
          <template
            v-if="!isCompact && appName === 'stocks' && modelName === 'samples'"
          >
            <ContextSetter
              :context-model-choices="contextModelChoices"
              :context="appliedContext"
              @select-context-model="
                ({ appName, modelName, modelType }) => {
                  contextModel = { appName, modelName, modelType }
                  isOpenContextSetterModal = true
                }
              "
              @unset-context="() => setContext(null)"
            />
            <BaseButton
              v-if="modelType !== 'total'"
              v-tooltip="mergeSamplesTooltip"
              size="small"
              variant="primary"
              :outlined="true"
              :class="[
                !hasChangeAndDeleteOnSelectedRecords ? 'is-warning' : '',
              ]"
              :disabled="
                selectedRecords.length <= 1 ||
                !hasChangeAndDeleteOnSelectedRecords
              "
              @click="isOpenMergeSampleModal = true"
            >
              <template #icon>
                <i class="fa-solid fa-vials" />
              </template>
              <i class="fa-solid fa-angles-right fa-xs" />
              <template #right-icon>
                <i class="fa-solid fa-vial" />
              </template>
            </BaseButton>
          </template>
          <template v-if="showOwnerControls">
            <span v-if="!isDesktopLargeView" v-tooltip="ownershipTooltip">
              <DropdownButton
                :label="ownershipLabel"
                :icon="ownershipIcon"
                size="small"
                variant="primary"
                outlined
                align="end"
                :disabled="isLoading"
                style="max-width: 220px"
              >
                <a
                  class="dd-item"
                  :class="{
                    'is-active': ownership.name.toLowerCase() === 'personal',
                  }"
                  @click="ownership = { name: 'personal' }"
                >
                  <span class="icon">
                    <i class="fa-solid fa-user" />
                  </span>
                  My items
                </a>
                <div class="dd-section">
                  <div class="dd-section-label">Groups</div>
                  <a
                    v-for="own in loggedInUser.groups"
                    :key="`own-opt-${own.name}${own.id}`"
                    class="dd-item"
                    :class="{
                      'is-active': own.id
                        ? own.id === ownership.id
                        : own.name === ownership.name,
                    }"
                    @click="ownership = own"
                  >
                    <span class="icon">
                      <i class="fa-solid fa-users" />
                    </span>
                    {{ $filters.capitalizeWords(own.name || '') }}
                  </a>
                  <a
                    class="dd-item"
                    :class="{
                      'is-active': ownership.name.toLowerCase() === 'all',
                    }"
                    @click="ownership = { name: 'all' }"
                  >
                    <span class="icon">
                      <i class="fa-solid fa-globe" />
                    </span>
                    All
                  </a>
                </div>
              </DropdownButton>
            </span>
            <div
              v-else
              v-tooltip="ownershipDesktopTooltip"
              class="buttons has-addons ownership-dropdown"
            >
              <button
                class="button is-small"
                :disabled="isLoading"
                :class="{
                  'is-primary is-selected': ownership.name === 'personal',
                }"
                @click="ownership = { name: 'personal' }"
              >
                My items
              </button>
              <DropdownButton v-if="loggedInUser.groups.length > 1" align="end">
                <template #trigger>
                  <button
                    class="button button-ellipsis is-small"
                    style="margin-right: -1px"
                    :class="{ 'is-primary is-selected': ownership.id }"
                  >
                    <span>
                      {{
                        (ownership.id && ownership.name) ||
                        loggedInUserPrimaryGroup.name
                      }}
                    </span>
                    <span class="icon is-small">
                      <i
                        class="fa-solid fa-angle-down"
                        style="font-size: 0.7em; opacity: 0.5"
                      />
                    </span>
                  </button>
                </template>
                <div class="dd-section-label">Groups</div>
                <a
                  v-for="group in loggedInUser.groups"
                  :key="`opt-${group.id}`"
                  class="dd-item"
                  :class="{
                    'is-active': ownership.id === group.id,
                  }"
                  @click="ownership = group"
                >
                  <span class="icon">
                    <i class="fa-solid fa-users" />
                  </span>
                  {{ $filters.capitalizeWords(group.name || '') }}
                </a>
              </DropdownButton>
              <button
                v-else
                class="button button-ellipsis is-small"
                :disabled="isLoading"
                :class="{
                  'is-primary is-selected':
                    ownership.id != null &&
                    ownership.id === loggedInUserPrimaryGroup.id,
                }"
                @click="ownership = loggedInUserPrimaryGroup"
              >
                <span>
                  {{
                    $filters.capitalizeWords(
                      loggedInUserPrimaryGroup.name || '',
                    )
                  }}
                </span>
              </button>
              <button
                class="button is-small"
                :disabled="isLoading"
                :class="{ 'is-primary is-selected': ownership.name === 'all' }"
                @click="ownership = { name: 'all' }"
              >
                All
              </button>
            </div>
          </template>
        </div>
      </div>
      <!-- Row 2: [left] Pagination + selected  [right] Quick actions -->
      <div class="bottom-row">
        <div class="bottom-left">
          <Pagination
            :current="currentPage"
            :total="totalPages"
            :loading="isLoadingRecords"
            :compact="!isDesktopLargeView"
            @page-change="changePage"
          />
          <Loader v-if="isLoadingRecords" />
          <template v-else>
            <DropdownButton
              :label="`${startIndex}-${endIndex} of ${totalRecords}`"
              :disabled="totalRecords < 1"
              variant="ghost"
              size="small"
              fit-content
              align="end"
            >
              <a
                v-for="n in [10, 50, 100, 500]"
                :key="n"
                class="dd-item"
                :class="{ 'is-active': n === pageSize }"
                @click="pageSize = n"
              >
                {{ n }}
              </a>
            </DropdownButton>
          </template>
          <span
            v-if="selectedCount > 0"
            ref="selectedTag"
            class="selected-count tag"
            @click="toggleSelectionHint"
          >
            {{ selectedCount }} selected
          </span>
        </div>
        <div class="bottom-right list-quick-actions">
          <span class="quick-action-group">
            <span class="gp-content">
              <span class="filter-split">
                <button
                  v-tooltip="
                    showFilters ? 'Hide column filters' : 'Show column filters'
                  "
                  class="filter-toggle-btn"
                  :class="{ 'is-active': showFilters }"
                  @click="toggleShowFilters"
                >
                  <span class="icon"><i class="fa-solid fa-filter" /></span>
                  <span class="icon icon-eye">
                    <i
                      :class="
                        showFilters
                          ? 'fa-solid fa-eye'
                          : 'fa-solid fa-eye-slash'
                      "
                    />
                  </span>
                </button>
                <IconButton
                  icon="fa-solid fa-angle-down"
                  :colorful="hasFilters"
                  :badge="hasFilters ? Object.keys(filters).length : null"
                  color="primary"
                  :show-arrow="false"
                  align="end"
                >
                  <template #dropdown>
                    <div v-if="hasFilters" class="p-3" style="min-width: 14rem">
                      <div class="is-flex is-align-items-center mb-3">
                        <span class="tag is-primary mr-2">
                          {{ Object.keys(filters).length }}
                          {{ pluralize('filter', Object.keys(filters).length) }}
                          set
                        </span>
                        <span style="margin-left: auto">
                          <a
                            class="has-text-danger"
                            @click.prevent="clearFilters"
                          >
                            Remove all
                          </a>
                        </span>
                      </div>
                      <ul class="filter-list">
                        <li
                          v-for="(fk, index) of Object.keys(filters)"
                          :key="`               filt-${fk}${index}`"
                        >
                          <div class="is-flex is-align-items-center">
                            <span
                              class="is-underlined has-text-weight-semibold mr-2"
                            >
                              {{ columnsById[fk].label }}:
                            </span>
                            <code>{{ filterValues[fk] }}</code>
                            <span style="margin-left: auto">
                              <a
                                class="delete is-small"
                                @click="removeFilter(fk)"
                              ></a>
                            </span>
                          </div>
                        </li>
                      </ul>
                    </div>
                    <div v-else class="p-3" style="min-width: 10rem">
                      <p class="has-text-grey">No filters active</p>
                    </div>
                  </template>
                </IconButton>
              </span>
            </span>
            <span class="sep icon is-small">
              <i class="fa-solid fa-circle" />
            </span>
            <span class="gp-content is-flex">
              <IconButton
                v-if="
                  modelType !== 'total' &&
                  (isBatchImportable ||
                    isBatchExportable ||
                    isBatchEditableWithTemplate) &&
                  !isCompact
                "
                icon="fa-solid fa-download"
                tooltip="Download / Export"
                align="end"
              >
                <template #dropdown>
                  <a
                    v-if="isBatchImportable"
                    v-tooltip="BATCH_CREATE_TOOLTIP"
                    class="dd-item"
                    @click="isOpenBatchCreateGetTemplateModal = true"
                    @keyup.enter="isOpenBatchCreateGetTemplateModal = true"
                  >
                    Download template for batch
                    <b>create</b>
                  </a>
                  <a
                    v-if="isBatchEditableWithTemplate"
                    v-tooltip="BATCH_EDIT_TOOLTIP"
                    class="dd-item"
                    @click="isOpenExportModal3 = true"
                    @keyup.enter="isOpenExportModal3 = true"
                  >
                    Download template for batch
                    <b>edit</b>
                  </a>
                  <a
                    v-if="isBatchExportable"
                    v-tooltip="
                      'Obtain a spreadsheet (excel) from loaded records'
                    "
                    class="dd-item"
                    :class="{ 'is-disabled': totalRecords < 1 }"
                    @click="isOpenExportModal2 = true"
                    @keyup.enter="isOpenExportModal2 = true"
                  >
                    <b>Export</b>
                    Items
                  </a>
                </template>
              </IconButton>
              <IconButton
                v-if="
                  modelType !== 'total' &&
                  (isBatchImportable || isBatchEditableWithTemplate) &&
                  !isCompact
                "
                icon="fa-solid fa-upload"
                tooltip="Upload / Import"
                align="end"
              >
                <template #dropdown>
                  <a
                    v-if="isBatchImportable"
                    class="dd-item"
                    @click="
                      $router.push({
                        name: 'batchImport',
                        params: { appName, modelName },
                      })
                    "
                    @keyup.enter="
                      $router.push({
                        name: 'batchImport',
                        params: { appName, modelName },
                      })
                    "
                  >
                    Batch
                    <b>create</b>
                    with spreadsheet
                  </a>
                  <a
                    v-if="isBatchEditableWithTemplate"
                    class="dd-item"
                    @click="
                      $router.push({
                        name: 'editTemplateImport',
                        params: { appName, modelName },
                      })
                    "
                    @keyup.enter="
                      $router.push({
                        name: 'editTemplateImport',
                        params: { appName, modelName },
                      })
                    "
                  >
                    Batch
                    <b>edit</b>
                    with spreadsheet
                  </a>
                </template>
              </IconButton>
            </span>
            <span class="sep icon is-small">
              <i class="fa-solid fa-circle" />
            </span>
          </span>
          <span v-if="showQuickActions" class="quick-action-group">
            <span class="gp-content">
              <BatchActions
                :disabled="!hasSelectedItems"
                :handle-add-to-order="
                  (isOrderable && canAddOrder && handleAddToOrder) || null
                "
                :handle-edit="
                  (canEdit &&
                    isBatchEditable &&
                    (() => {
                      batchEditContext = ''
                      handleEdit()
                    })) ||
                  null
                "
                :handle-share="
                  (canEdit &&
                    isBatchEditable &&
                    (() => {
                      batchEditContext = 'share'
                      handleEdit()
                    })) ||
                  null
                "
                :handle-copy="
                  (doesSupportDatasetCopy && toggleDatasetCopyModal) || null
                "
                :handle-archive="
                  (isArchivable && canArchive && handleArchive) || null
                "
                :handle-restore="
                  (isRestorable && canArchive && handleRestore) || null
                "
                :handle-change-order-status="
                  (canAddOrder &&
                    isOrderedItemsList &&
                    toggleBatchEditOrderStatusModal) ||
                  null
                "
                :handle-delete="
                  (canDelete &&
                    isBatchDeletable &&
                    !isBatchRemovable &&
                    !isBatchUnlinkable &&
                    toggleBatchDeleteModal) ||
                  null
                "
                :handle-unlink="
                  (isBatchUnlinkable &&
                    (handleBatchUnlink
                      ? handleBatchUnlink
                      : toggleBatchUnlinkModal)) ||
                  null
                "
                :handle-remove-datafilecopies="
                  (isBatchRemovable && toggleBatchRemoveModal) || null
                "
                :handle-free-disk-space="
                  (doesSupportFreeDiskSpace && toggleDeleteDatasetsModal) ||
                  null
                "
              />
            </span>
            <span class="sep icon is-small">
              <i class="fa-solid fa-circle" />
            </span>
          </span>
          <span class="quick-action-group">
            <IconButton
              v-if="isAnnotatable"
              icon="fa-solid fa-tags"
              :tooltip="annotationsTooltip"
              :badge="nonVisibleAnnotationsWithValue.length > 0 ? '' : null"
              :disabled="nonVisibleAnnotationsWithValue.length === 0"
              @click="
                () =>
                  showColumns({
                    ids: nonVisibleAnnotationsWithValue
                      .reverse()
                      .map(({ annotation_type_id }) => annotation_type_id),
                  })
              "
            />
            <IconButton
              v-if="getNextCodeUrl"
              icon="fa-solid fa-forward-step"
              tooltip="Get next code in item serie"
              @click="handleGetNextCode"
            />
            <IconButton
              icon="fa-solid fa-columns"
              tooltip="Column settings"
              @click="handleClickColumnSettings"
            />
            <IconButton
              v-if="canSaveSettings"
              icon="fa-solid fa-save"
              tooltip="Save table settings"
              @click="handleSaveSettings"
            />
            <IconButton
              icon="fa-solid fa-ellipsis-v"
              tooltip="Display settings"
              align="end"
              :show-arrow="false"
            >
              <template #dropdown>
                <div class="dd-section-label">Display settings</div>
                <a class="dd-item" @click="loadServerSettings()">
                  Load user settings
                  <span class="icon">
                    <i class="fa-solid fa-user-cog" />
                  </span>
                </a>
                <a class="dd-item" @click="loadDefaultSettings()">
                  Load default settings
                  <span class="icon">
                    <i class="fa-solid fa-undo" />
                  </span>
                </a>
              </template>
            </IconButton>
          </span>
        </div>
      </div>
      <slot
        v-if="
          hasLoadedOnce &&
          !isLoadingRecords &&
          !isRefreshing &&
          records &&
          records.length === 0
        "
        name="empty-state"
        v-bind="{
          ownership,
          loggedInUser,
          hasSearchSet,
          hasSessionFilters,
          clearFilters,
          clearSearch,
          isLoadingRecords,
          isRefreshing,
          hasLoadedOnce,
        }"
      >
        <div class="notification is-info is-fullwidth">
          <span class="icon">
            <i class="fa-solid fa-info-circle" />
          </span>
          <div class="text">
            <p>
              No data
              <span v-if="ownership.name === 'personal'">
                belonging to
                <em>{{ loggedInUser.username }}</em>
              </span>
              <span v-else-if="ownership.id">
                belonging to
                <em>{{ ownership.name }}</em>
              </span>
            </p>
            <p v-if="ownership.name !== 'all'">
              <span>
                You are attempting to load {{ ownership.name }} items.
              </span>
              <span>
                Try
                <span v-if="ownership.name === 'personal'">
                  viewing
                  <a
                    class="is-primary has-text-primary"
                    tabindex="0"
                    @click="ownership = loggedInUserPrimaryGroup"
                  >
                    group items
                  </a>
                  instead
                </span>
                <span
                  v-else-if="
                    ownership.name !== 'all' && ownership.name !== 'personal'
                  "
                >
                  viewing
                  <a
                    class="is-primary has-text-primary"
                    tabindex="0"
                    @click="ownership = { name: 'all' }"
                  >
                    all items
                  </a>
                  instead.
                </span>
              </span>
            </p>
            <div v-if="hasSearchSet || hasSessionFilters">
              It appears you are filtering the data:
              <ol class="p-0" style="display: inline; list-style: decimal">
                <li v-if="hasSessionFilters" style="display: inline">
                  <span>
                    try
                    <a
                      class="is-primary has-text-primary"
                      tabindex="0"
                      @click="clearFilters"
                    >
                      clearing search filters
                    </a>
                  </span>
                </li>
                <li v-if="hasSearchSet" style="display: inline">
                  <span>
                    try
                    <a
                      class="is-primary has-text-primary"
                      tabindex="0"
                      @click="clearSearch"
                    >
                      clearing global search
                    </a>
                  </span>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </slot>
    </div>
    <DataTable
      class="data-table"
      :selection-mode="selectionMode"
      :change-sort="(s) => (sort = s)"
      :clear-filters="clearFilters"
      :columns="visibleColumns"
      :records="records"
      :filters="
        Object.keys(filters).reduce((filts, filtId) => {
          filts[filtId] = {
            value: filters[filtId],
            // currently not in use
            readonly: false,
          }
          return filts
        }, {})
      "
      :set-filter="setFilter"
      :unset-filter="removeFilter"
      :show-filters="showFilters"
      :sort="sort"
      :is-loading="isLoadingRecords"
      :is-refreshing="isRefreshing"
      :max-records="pageSize"
      :app-name="appName"
      :model-name="modelName"
      @selected="(r) => (selectedRecords = r)"
      @toggle-filters="showFilters = true"
      @move-column="
        ({ fromIndex, toIndex }) => {
          moveColumns({ fromIndices: [fromIndex], toIndex })
        }
      "
      @remove-column="removeColumnByIndex"
    >
      <template v-for="(_, name) in $slots" #[name]="slotData">
        <slot :name="name" v-bind="slotData" />
      </template>
    </DataTable>
    <RightOverlay :opened="rightOverlayOpen" @close="rightOverlayOpen = false">
      <DataTableColumnSettings
        v-if="rightOverlayOpen"
        :key="`column-settings-${rightOverlayOpen ? 'opened' : 'closed'}`"
        :columns-data="columnsById"
        :savable="canSaveSettings"
        :visible-column-ids="visibleColumnIds"
        :annotations-with-value="nonVisibleAnnotationsWithValue"
        :handle-save-settings="(canSaveSettings && handleSaveSettings) || null"
        @toggle-column="({ id, index }) => handleToggleColumnById(id, index)"
        @toggle-columns="
          ({ ids, toIndex }) => handleToggleColumnById(ids, index)
        "
        @toggle-all-columns="handleToggleAllColumns"
        @move-columns="moveColumns"
        @add-columns="showColumns"
        @remove-column="removeColumnByIndex"
      ></DataTableColumnSettings>
    </RightOverlay>
    <ModalCard
      v-if="isOpenChangeOrderStatusModal"
      title="Change Order Status"
      :handle-close="toggleBatchEditOrderStatusModal"
    >
      <template #body>
        <div style="height: 250px">
          <BatchEditOrderStatus
            @input="
              (newStatus) => {
                batchOrderStatus = newStatus
              }
            "
          />
        </div>
      </template>
      <template #footer>
        <a class="button is-outlined" @click="toggleBatchEditOrderStatusModal">
          Cancel
        </a>
        <a
          class="button is-outlined is-primary"
          @click="
            () => {
              toggleBatchEditOrderStatusModal()
              editOrderStatus()
            }
          "
        >
          Change status
        </a>
      </template>
    </ModalCard>
    <BatchEditModal
      v-if="isOpenBatchEditModal"
      v-model:is-open="isOpenBatchEditModal"
      :app-name="appName"
      :model-name="modelName"
      :model-type="modelType"
      :records="selectedRecords"
      :allow-annotations="allowsAnnotations"
      :is-permission-context="batchEditContext === 'share'"
      @done="loadData"
    ></BatchEditModal>
    <ModalCard
      v-if="isArchiveModalOpened"
      title="Batch Archive Items"
      :handle-close="toggleArchiveModal"
      width="75%"
      height="95%"
    >
      <template #body>
        <div style="height: 100%">
          <BatchArchive
            :app-name="appName"
            :model-name="modelName"
            :model-type="modelType"
            :datafiles="archivableDatafiles"
            :selected-items="selectedRecords"
            @batch-edit-success="batchEditSuccess = $event"
          />
        </div>
      </template>
      <template #footer>
        <div style="width: 100%; text-align: right">
          <a class="button is-outlined is-small" @click="toggleArchiveModal">
            Close
          </a>
        </div>
      </template>
    </ModalCard>
    <BatchRestoreModal
      v-model:is-opened="isRestoreModalOpened"
      :app-name="appName"
      :model-name="modelName"
      :model-type="modelType"
      :records="selectedRecords"
    />
    <DatasetCopyModal
      v-if="isDatasetCopyModalOpened"
      v-model:is-opened="isDatasetCopyModalOpened"
      :records="selectedRecords"
    />
    <ModalCard
      v-if="isOpenBatchDeleteModal"
      title="Please confirm"
      :handle-close="toggleBatchDeleteModal"
    >
      <template #body>
        <div>
          <span class="icon">
            <i class="fa-solid fa-exclamation-triangle" />
          </span>
          Do you really want to delete the {{ selectedCount }} selected items?
        </div>
      </template>
      <template #footer>
        <a class="button is-outlined" @click="toggleBatchDeleteModal">Cancel</a>
        <a
          class="button is-outlined is-danger"
          @click="
            () => {
              handleDeleteSelected()
              toggleBatchDeleteModal()
            }
          "
        >
          Yes, delete
        </a>
      </template>
    </ModalCard>
    <ModalCard
      v-if="isOpenBatchUnlinkModal && !handleBatchUnlink"
      title="Please confirm"
      class="is-active"
      :handle-close="toggleBatchUnlinkModal"
    >
      <template #body>
        <div>
          <span class="icon">
            <i class="fa-solid fa-exclamation-triangle" />
          </span>
          You are about to unlink the selected items.
        </div>
      </template>
      <template #footer>
        <a class="button is-outlined" @click="toggleBatchUnlinkModal">Cancel</a>
        <a class="button is-outlined is-danger" @click="handleUnlinkSelected">
          Yes, unlink
        </a>
      </template>
    </ModalCard>
    <ModalCard
      v-if="isOpenBatchRemoveModal"
      title="Please confirm"
      :handle-close="toggleBatchRemoveModal"
    >
      <template #body>
        <div>
          <span class="icon">
            <i class="fa-solid fa-exclamation-triangle" />
          </span>
          You are about to permanently remove file(s) to free this disk space.
          <br />
          <b class="has-text-danger">
            Once removed, files can not be recovered unless a back up copy is
            available.
          </b>
        </div>
      </template>
      <template #footer>
        <a class="button is-outlined" @click="toggleBatchRemoveModal">Cancel</a>
        <a
          class="button is-outlined is-danger"
          :class="{ 'is-loading': isRemovingDatafilecopies }"
          @click="handleRemoveDatafilecopies"
        >
          Yes, Remove
        </a>
      </template>
    </ModalCard>
    <ModalCard
      v-if="isDeleteDatasetsModalOpened"
      title="Free Disk Space"
      :handle-close="toggleDeleteDatasetsModal"
      width="75%"
      height="95%"
    >
      <template #body>
        <div style="height: 100%">
          <BatchDeleteDatasets
            ref="deleteDatasets"
            :app-name="appName"
            :model-name="modelName"
            :model-type="modelType"
            :records-id="selectedRecordIds"
            @deleted="handleDatasetsDeletionDone"
            @button-status="setDatasetsDeletionButtonStatus"
          />
        </div>
      </template>
      <template #footer>
        <div style="width: 100%; text-align: right">
          <div
            v-tooltip="datasetsDeletionButtonStatus.reason"
            class="footer-btn-container"
          >
            <button
              :class="{ 'is-loading': isDeletingDatasets }"
              :disabled="
                isDeletingDatasets || datasetsDeletionButtonStatus.disabled
              "
              class="button is-primary is-small"
              @click="triggerDatasetsRemoval"
            >
              Remove
            </button>
          </div>
        </div>
      </template>
    </ModalCard>
    <ModalCard
      v-if="isGetNextCodeModalOpened"
      title="Get next unique code for item serie"
      :handle-close="() => (isGetNextCodeModalOpened = false)"
    >
      <template #body>
        <GetIncrementedValue
          field-label="code"
          :url="getNextCodeUrl"
          :group="
            ownership.id
              ? ownership
              : loggedInUserPrimaryGroup
                ? loggedInUserPrimaryGroup
                : null
          "
          :value="firstCode"
          :show-advanced-settings="true"
        ></GetIncrementedValue>
      </template>
      <template #footer>
        <div style="width: 100%; text-align: right">
          <button
            class="button is-primary is-small"
            @click="() => (isGetNextCodeModalOpened = false)"
          >
            Done
          </button>
        </div>
      </template>
    </ModalCard>
    <ExportModal
      v-model:is-open="isOpenExportModal2"
      :model-name="modelName"
      :url="
        `${appName}/${modelName}/export/${modelType}/?` +
        [ownershipFilterUrlParams, orderingUrlParam, contextUrlParam]
          .filter((u) => u !== null)
          .join('&')
      "
      :selected-record-ids="selectedRecordIds"
      :total-records="totalRecords"
      :selected-records-annotation-ids="selectedRecordsAnnotationIds"
      :visible-attribute-ids="visibleAttributeIds"
      :total-attributes="attributeColumns.length"
      :allows-annotations="allowsAnnotations"
      :visible-annotations-ids="visibleAnnotationsIds"
      :relevant-annotations="relevantAnnotations"
      :query-filters="queryFilters"
    />
    <BatchEditGetTemplateModal
      v-model:is-open="isOpenExportModal3"
      :model-name="modelName"
      :url="
        `${appName}/${modelName}/export/${modelType}/?template=True&` +
        [ownershipFilterUrlParams, contextUrlParam]
          .filter((u) => u !== null)
          .join('&')
      "
      :selected-records="selectedRecords"
      :all-records="{ count: totalRecords, queryFilters }"
      :selected-records-annotations="selectedRecordsAnnotationsModels"
      :annotations="annotations"
      :relevant-annotations="relevantAnnotations"
    />
    <BatchCreateGetTemplateModal
      v-model:is-open="isOpenBatchCreateGetTemplateModal"
      :item-type="modelType"
      :base-url="`${appName}/${modelName}/template/${modelType}/?`"
      :customisable="allowsAnnotations"
      :relevant-annotations="
        (hasSelectedItems && selectedRecordsAnnotationsModels) || []
      "
      :annotations="annotations"
    />
    <ListMergeSamplesModal
      v-model:is-open="isOpenMergeSampleModal"
      :sample-meta-model="(meta && meta.model) || {}"
      :samples="selectedRecords"
      :permissions-by-id="selectedRecordsPermissionsByIds"
      :columns="
        modelColumns.map((c) => {
          return {
            ...c,
            isVisible:
              c.id === 'name' ||
              c.id === 'created' ||
              selectedRecordsAnnotationIds.indexOf(c.id) !== -1 ||
              visibleColumnIds.indexOf(c.id) !== -1,
          }
        })
      "
      @merged-samples="
        () => {
          isOpenMergeSampleModal = false
          loadData()
        }
      "
    />
    <ContextSetterModal
      v-model:is-open="isOpenContextSetterModal"
      :init-context="contextModel"
      :context-model-choices="contextModelChoices"
      @update-context="
        (c) => {
          setContext(c)
          isOpenContextSetterModal = false
        }
      "
    />
  </div>
</template>

<script>
import { useToggle } from '@vueuse/core'
import { cloneDeep, debounce, get as _get, isObject, set } from 'lodash-es'
import pluralize from 'pluralize'
import { v4 as uuid } from 'uuid'
import { computed, defineAsyncComponent, ref, watch } from 'vue'
import { mapActions, mapGetters, mapMutations, mapState, useStore } from 'vuex'

import BatchActions from '@/components/listings/BatchActions.vue'
import BatchArchive from '@/components/listings/BatchArchive.vue'
import BatchEditModal from '@/components/listings/BatchEditModal.vue'
import BatchEditOrderStatus from '@/components/listings/BatchEditOrderStatus.vue'
import BatchRestoreModal from '@/components/listings/BatchRestoreModal.vue'
import ContextSetter from '@/components/listings/ContextSetter.vue'
const ContextSetterModal = defineAsyncComponent(
  () => import('@/components/listings/ContextSetterModal.vue'),
)
import tippy from 'tippy.js'

import { del, get, patch, post, put } from '@/api'
import { hasPrivilege } from '@/auth/authorization'
import { useBreakpoints } from '@/composables/layouts/useBreakpoints'
import { useDetailViewCommons } from '@/composables/useDetailViewCommons'
import { SELECTION_MODES } from '@/composables/useSelectableRecords'
import { capitalizeWords } from '@/filters/filters'
import serverSettingsModule from '@/store/modules/settings/server.store.js'
import sessionSettingsModule from '@/store/modules/settings/session.store.js'
import { mapModelType, matchUuid, modelNameSingularize } from '@/utils'
import { getModelApiBaseUrl } from '@/utils/apiPaths'
import { getFileFlags } from '@/utils/fileFlags'
import { buildPatchedAttributes } from '@/utils/modelMetaPatches'
import { MOD_KEY } from '@/utils/platform'

import BatchDeleteDatasets from '@/components/BatchDeleteDatasets.vue'
import CreateItemButton from '@/components/CreateItemButton.vue'
import GetIncrementedValue from '@/components/GetIncrementedValue.vue'
import BatchCreateGetTemplateModal from '@/components/listings/BatchCreateGetTemplateModal.vue'
import BatchEditGetTemplateModal from '@/components/listings/BatchEditGetTemplateModal.vue'
import DatasetCopyModal from '@/components/listings/DatasetCopyModal.vue'
import DataTable from '@/components/listings/DataTable.vue'
import DataTableColumnSettings from '@/components/listings/DataTableColumnSettings.vue'
import ExportModal from '@/components/listings/ExportModal.vue'
import ListMergeSamplesModal from '@/components/listings/ListMergeSamplesModal.vue'
import RightOverlay from '@/components/RightOverlay.vue'

const SELECTION_HINT_HTML =
  `<span class="hint-row"><kbd>${MOD_KEY} Click</kbd> on row to multi-select</span>` +
  '<span class="hint-row"><kbd>⇧ Click</kbd> on row to select range</span>' +
  '<span class="hint-row"><kbd>Drag</kbd> rows to select a range</span>' +
  '<span class="hint-row hint-row--note">Clicking checkboxes adds to current selection</span>'

const CONTEXT_MODEL_CHOICES = Object.freeze([
  { appName: 'core', modelName: 'projects', modelType: 'default' },
  { appName: 'core', modelName: 'studies', modelType: 'default' },
  { appName: 'assays', modelName: 'assays', modelType: 'total' },
])

// Static tooltip objects — defined once at module level to avoid
// creating new object references on every render (which triggers
// infinite reactivity loops in floating-vue v5).
const BATCH_CREATE_TOOLTIP = {
  content: `Obtain a spreadsheet (excel) template prefilled with columns
    information. Later upload the filled in spreadsheet to <b>batch create</b>
    the items`,
  delay: { hide: 0 },
  popperClass: 'is-shallow',
}
const BATCH_EDIT_TOOLTIP = {
  content: `Obtain a spreadsheet (excel) prefilled with existing items
    information. Later upload the updated spreadsheet to <b>batch edit</b>
    the items`,
  delay: { hide: 0 },
  popperClass: 'is-shallow',
}

export default {
  name: 'ListComponent',
  inject: { containerState: { default: null } },
  emits: ['init', 'selected', 'loading', 'error-state'],
  components: {
    BatchActions,
    BatchArchive,
    BatchEditModal,
    BatchEditOrderStatus,
    BatchCreateGetTemplateModal,
    BatchEditGetTemplateModal,
    BatchRestoreModal,
    CreateItemButton,
    ContextSetter,
    ContextSetterModal,
    DataTable,
    DataTableColumnSettings,
    DatasetCopyModal,
    ExportModal,
    ListMergeSamplesModal,
    RightOverlay,
    BatchDeleteDatasets,
    GetIncrementedValue,
  },
  props: {
    vuexPrefix: {
      type: String,
      default: '',
    },
    appName: {
      type: String,
      required: true,
    },
    modelName: {
      type: String,
      required: true,
    },
    modelType: {
      type: String,
      default: 'DEFAULT',
    },
    showAddControls: {
      type: Boolean,
      default: false,
    },
    showOwnerControls: {
      type: Boolean,
      default: false,
    },
    showQuickActions: {
      type: Boolean,
      default: true,
    },
    showGlobalSearch: {
      type: Boolean,
      default: false,
    },
    canSaveSettings: {
      type: Boolean,
      default: false,
    },
    selectionMode: {
      type: String,
      default: 'select',
      validator: (v) => SELECTION_MODES.includes(v),
    },
    isCompact: {
      type: Boolean,
      default: false,
    },
    isBatchUnlinkable: {
      type: Boolean,
      default: false,
    },
    handleBatchUnlink: {
      type: Function,
      default: null,
    },
    initContext: {
      type: Object,
      default: () => ({ key: '', id: '' }),
    },
    initFilters: {
      type: Object,
      default: null,
    },
    initOwnership: {
      type: [Number, String],
      default: null,
    },
    columnNames: {
      type: Array,
      default: () => [],
    },
    showDeleted: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const store = useStore()
    const meta = computed(
      () =>
        store.getters['meta/getMeta'](
          props.appName,
          props.modelName,
          props.modelType,
        ) || {},
    )
    const {
      allowsAnnotations,
      allowsAttachments,
      allowsOrders,
      allowsNotes,
      allowsShipments,
    } = useDetailViewCommons(meta)

    const selectedRecords = ref([])
    const isRestorable = computed(
      () => props.modelName === 'archives' || props.modelName === 'datasets',
      // _get(this.modelNameActions, 'archivable', false)
    )
    const canRestore = computed(
      () => hasPrivilege('data_management', 'archive', 'restore') || true,
    )
    const [isRestoreModalOpened, toggleRestoreModal] = useToggle(false)

    const { isDesktopLargeView } = useBreakpoints()

    // --- vuexSettingsModules mixin logic (inlined) ---
    const initialisedSettingModules = ref(false)

    const vuexUid = computed(() =>
      [props.vuexPrefix, props.appName, props.modelName, props.modelType]
        .join('')
        .toLowerCase(),
    )
    const serverSettingsPathPrefix = computed(
      () => `serverSettings/${vuexUid.value}`,
    )
    const sessionSettingsPathPrefix = computed(
      () => `sessionSettings/${vuexUid.value}`,
    )

    const storeModuleExists = (storeKey) =>
      store.state?.[storeKey]?.[vuexUid.value]

    const initStore = (storeKey, storeModule) => {
      const moduleId = [storeKey, vuexUid.value]
      store.registerModule(moduleId, storeModule)
      const unregisterHook = () => {
        store.unregisterModule(moduleId)
        delete store.state[moduleId[0]][moduleId[1]]
      }
      store.commit('addUnregisterModuleHooks', unregisterHook)
    }

    const initModules = async () => {
      if (!storeModuleExists('serverSettings')) {
        initStore('serverSettings', serverSettingsModule)
        await store.dispatch(`${serverSettingsPathPrefix.value}/initModule`, {
          vuexPrefix: props.vuexPrefix,
          appName: props.appName,
          modelName: props.modelName,
          modelType: props.modelType.toLowerCase(),
        })
      }
      if (!storeModuleExists('sessionSettings')) {
        initStore('sessionSettings', sessionSettingsModule)
        await store.dispatch(`${sessionSettingsPathPrefix.value}/initModule`, {
          appName: props.appName,
          modelName: props.modelName,
          modelType: props.modelType.toLowerCase(),
          vuexUid: vuexUid.value,
        })
      }
      initialisedSettingModules.value = true
    }

    watch(
      () => store.state.settings?.baseSettingsInitialised,
      (newInit) => {
        if (newInit) initModules()
      },
      { immediate: true },
    )

    return {
      meta,
      allowsAnnotations,
      allowsAttachments,
      allowsOrders,
      allowsNotes,
      allowsShipments,
      selectedRecords,
      isRestorable,
      canRestore,
      isRestoreModalOpened,
      toggleRestoreModal,
      isDesktopLargeView,
      initialisedSettingModules,
      vuexUid,
      serverSettingsPathPrefix,
      sessionSettingsPathPrefix,
      storeModuleExists,
      initStore,
      initModules,
    }
  },
  data() {
    return {
      rightOverlayOpen: false,
      isOpenEllipsisMenu: false,
      isLoading: true,
      records: null,
      isOpenBatchDeleteModal: false,
      isOpenBatchRemoveModal: false,
      isOpenBatchUnlinkModal: false,
      isOpenChangeOrderStatusModal: false,
      isOpenExportModal2: false,
      isOpenExportModal3: false,
      isOpenBatchEditModal: false,
      isOpenMergeSampleModal: false,
      isOpenContextSetterModal: false,
      isArchiveModalOpened: false,
      isOpenBatchCreateGetTemplateModal: false,
      isGetNextCodeModalOpened: false,
      batchOrderStatus: '',
      abortCtlr: null,
      batchEditContext: '',
      batchEditSuccess: false,
      currentPage: 1,
      nextCurrentPage: 1,
      totalRecords: 0,
      totalPages: 0,
      isLoadingRecords: true,
      isRefreshing: false,
      hasLoadedOnce: false,
      hoveringSearch: false,
      lastSearch: '',
      activeLoadRequestId: 0,
      isFetchingAnnotations: false,
      relevantAnnotations: null,
      archivableDatafiles: [],
      contextModel: null,
      contextModelChoices: CONTEXT_MODEL_CHOICES,
      isDatasetCopyModalOpened: false,
      isDeletingDatasets: false,
      isRemovingDatafilecopies: false,
      isDeleteDatasetsModalOpened: false,
      datasetsDeletionButtonStatus: {
        disabled: true,
        reason: 'Please wait, loading resources...',
      },
      selectionHintShownOnce: false,
    }
  },
  computed: {
    ...mapGetters('auth', ['loggedInUser', 'loggedInUserPrimaryGroup']),
    ...mapGetters('meta', [
      'isMetaReady',
      'getMetaGlobalModelAttrValue',
      'annotationMetaModels',
      'annotationMetaViews',
    ]),
    ...mapGetters('meta', {
      annotationMetaModel: 'getAnnotationMetaModelByName',
    }),
    ...mapGetters('orders', ['orderedItemTemplate']),
    ...mapGetters('settings', {
      allSettings: 'rawSettings',
      _showFilters: 'showFilters',
    }),
    ...mapState({
      /* List store getters
       * MapState is used instead of MapGetters because the MapGetters syntax
       * does not allow for a variable ID */
      sessionVisibleColumnIds(state, getters) {
        return getters[this.sessionSettingsPathPrefix + '/visibleColumnIds']
      },
      sessionOwnership(state, getters) {
        return getters[this.sessionSettingsPathPrefix + '/ownership']
      },
      sessionSort(state, getters) {
        return getters[this.sessionSettingsPathPrefix + '/sort']
      },
      sessionPageSize(state, getters) {
        return getters[this.sessionSettingsPathPrefix + '/pageSize']
      },
      sessionSearch(state, getters) {
        return getters[this.sessionSettingsPathPrefix + '/search']
      },
      hasSearchSet(state, getters) {
        return getters[this.sessionSettingsPathPrefix + '/hasSearch']
      },
      sessionSearchVisibleOnly(state, getters) {
        return getters[this.sessionSettingsPathPrefix + '/searchVisibleOnly']
      },
      sessionFilters(state, getters) {
        return getters[this.sessionSettingsPathPrefix + '/filters']
      },
      hasSessionFilters(state, getters) {
        return getters[this.sessionSettingsPathPrefix + '/hasSessionFilters']
      },
      storedContext() {
        const context =
          this.$store.getters[this.sessionSettingsPathPrefix + '/context']
        return context
      },
      settingsToSave(state, getters) {
        return getters[this.sessionSettingsPathPrefix + '/settingsToSave']
      },
    }),

    initialised() {
      return (
        this.isMetaReady &&
        Object.keys(this.meta).length > 0 &&
        this.initialisedSettingModules
      )
    },
    ownership: {
      get() {
        return this.sessionOwnership || {}
      },
      set(own) {
        this.nextCurrentPage = 1
        this.changeOwnership(own)
        // if (own.id) this.selectOwnershipGroup(own)
        this.loadData()
      },
    },
    sort: {
      get() {
        return this.sessionSort
      },
      set(sort) {
        this.nextCurrentPage = 1
        this.changeSort(sort)
        this.loadData()
      },
    },
    pageSize: {
      get() {
        return this.sessionPageSize
      },
      set(pageSize) {
        this.nextCurrentPage = 1
        this.changePageSize(pageSize)
        this.loadData()
      },
    },
    search: {
      get() {
        return this.sessionSearch
      },
      set(search) {
        this.debounceSetSearch(search)
      },
    },
    searchVisibleOnly: {
      get() {
        return this.sessionSearchVisibleOnly
      },
      set(searchVisOnly) {
        this.setSearchVisibleOnly(searchVisOnly)
        this.loadData()
      },
    },
    showFilters: {
      get() {
        return this._showFilters
      },
      set(show) {
        this.setShowFilters(show)
      },
    },
    filters() {
      return this.sessionFilters || {}
    },
    filterValues() {
      // Stringify some complex filter value...
      return Object.entries(this.filters).reduce((filtVals, [filtK, filtV]) => {
        let value = filtV.value || filtV
        if (isObject(value))
          value = Object.entries(value).map(
            ([k, v]) => `${(k !== 'option' && `${k}:`) || ''}${v.value || v}`,
          )
        if (Array.isArray(value)) value = value.join(', ')
        filtVals[filtK] = value
        return filtVals
      }, {})
    },
    defaultFilters() {
      const defaultFilters = {}
      const hasDeletedCol = this.meta.model?.attributes?.find(
        (a) => a.name === 'deleted',
      )
      if (hasDeletedCol && !this.showDeleted) {
        defaultFilters.deleted = 'false'
      }
      return defaultFilters
    },
    hasFilters() {
      return Object.keys(this.filters).length > 0
    },
    initFiltersSetting() {
      let initFilters = {}
      if (this.initFilters !== null) {
        for (let filtKey in this.initFilters) {
          initFilters = {
            ...initFilters,
            ...(this.columnIds.includes(filtKey) && {
              [filtKey]: this.initFilters[filtKey],
            }),
          }
        }
      }
      return initFilters
    },
    initSettings() {
      return {
        defaultVisibleColumnIds: this.defaultVisibleColumnIds,
        defaultFilters: this.defaultFilters,
      }
    },
    modelNameActions() {
      return this.getMetaGlobalModelAttrValue(
        this.appName,
        modelNameSingularize(this.modelName),
        'actions',
      )
    },
    isBatchImportable() {
      return _get(this.modelNameActions, 'bulk_import', false)
    },
    isBatchExportable() {
      return _get(this.modelNameActions, 'bulk_export', false)
    },
    isBatchDeletable() {
      return _get(this.modelNameActions, 'bulk_delete', false)
    },
    isBatchRemovable() {
      return this.initContext.key === 'dataset_id'
    },
    isBatchEditable() {
      const canBatchEdit = this.modelNameActions?.bulk_edit === true
      const preventBatchEdit = this.modelType.toLowerCase() === 'total'
      return canBatchEdit && !preventBatchEdit
    },
    isBatchEditableWithTemplate() {
      return _get(this.modelNameActions, 'bulk_template_edit', false)
    },
    isAnnotatable() {
      return this.meta.model?.annotatable
    },
    isArchivable() {
      return _get(this.modelNameActions, 'archivable', false)
    },
    isWorkflowList() {
      return this.modelName === 'workflows'
    },
    isWorkflowRunList() {
      return this.modelName === 'workflowruns'
    },
    isOntologyList() {
      return this.appName === 'vocabularies' && this.modelName === 'ontologies'
    },
    isOntologyTermList() {
      return (
        this.appName === 'vocabularies' && this.modelName === 'ontologyterms'
      )
    },
    isCategoryList() {
      return this.appName === 'vocabularies' && this.modelName === 'categories'
    },
    isTermList() {
      return this.appName === 'vocabularies' && this.modelName === 'terms'
    },
    canAddOntology() {
      return hasPrivilege('vocabularies', 'ontology', 'add')
    },
    canAddOntologyTerm() {
      return hasPrivilege('vocabularies', 'ontologyterm', 'add')
    },
    canAddCategory() {
      return hasPrivilege('vocabularies', 'category', 'add')
    },
    canAddTerm() {
      return hasPrivilege('vocabularies', 'term', 'add')
    },
    canCreate() {
      return this.meta.model ? !this.meta.model.readonly : false
    },
    creatableModel() {
      const isMergedListView = this.modelType.toLowerCase() === 'total'
      const isDataManagement = this.appName === 'data_management'
      const isWorkflowVersion = this.modelName === 'workflowversions'
      return !isMergedListView && !isDataManagement && !isWorkflowVersion
    },
    canArchive() {
      return hasPrivilege('data_management', 'archive', 'add')
    },
    canAddOrder() {
      return hasPrivilege('stocks', 'order', 'add')
    },
    canEdit() {
      return hasPrivilege(this.appName, this.modelName, 'change')
    },
    canDelete() {
      return hasPrivilege(this.appName, this.modelName, 'delete')
    },
    doesSupportFreeDiskSpace() {
      return ['studies', 'datasets'].includes(this.modelName)
    },
    doesSupportDatasetCopy() {
      return ['assays', 'studies', 'projects', 'datasets'].includes(
        this.modelName,
      )
    },
    isOrderable() {
      return this.modelName === 'ordereditems' || this.meta.model?.orderable
    },
    isOrderedItemsList() {
      return this.modelName === 'ordereditems'
    },
    attributes() {
      return (
        this.meta?.view?.list?.attributes
          ?.concat()
          .sort((a, b) => a.order - b.order) || []
      )
    },
    attributeColumns() {
      const viewAttrsByName = Object.fromEntries(
        this.attributes.map((a) => [a.name, a]),
      )
      const combined = buildPatchedAttributes(
        this.meta.model?.attributes || [],
        viewAttrsByName,
        this.modelName,
        this.modelType,
      )
      const columns = []
      for (const fieldViewMeta of this.attributes) {
        const fieldMeta = combined[fieldViewMeta.name]
        if (!fieldMeta) {
          console.warn(
            `[ListComponent] attributeColumns: no model attribute for "${fieldViewMeta.name}" — column skipped`,
          )
          continue
        }
        columns.push({
          id: fieldViewMeta.name,
          label: fieldMeta.label,
          isVisible: fieldViewMeta.visible,
          isSearchable: fieldMeta.searchable,
          isSortable: fieldMeta.sortable,
          fieldMeta,
          category: 'attribute',
        })
      }
      return columns
    },
    annotations() {
      const annots = this.annotationMetaModels
        .filter((a) => !a.readonly)
        .map((a) => ({ label: a.label, value: a.id, name: a.name }))
      return annots
    },
    annotationColumns() {
      const columns = []
      if (this.isAnnotatable) {
        for (const annotationViewMeta of this.annotationMetaViews) {
          const annotationModel = this.annotationMetaModel(
            annotationViewMeta.name,
          )
          columns.push({
            id: annotationModel.id,
            label: annotationModel.label,
            isVisible: false,
            isSearchable: annotationModel.searchable,
            isSortable: annotationModel.sortable,
            fieldMeta: { ...annotationModel, ...annotationViewMeta },
            category: 'annotation',
          })
        }
      }
      return columns
    },
    modelColumns() {
      return [...this.attributeColumns, ...this.annotationColumns]
    },
    columnIds() {
      return this.modelColumns.map((c) => c.id)
    },
    defaultVisibleColumnIds() {
      const defaultVisibleIds = this.modelColumns
        .filter((a) => a.isVisible && !this.columnNames.includes(a.id))
        .map((a) => a.id)
      return [...this.columnNames, ...defaultVisibleIds].filter((cId) =>
        this.columnIds.includes(cId),
      )
    },
    visibleColumnIds() {
      return (
        this.sessionVisibleColumnIds?.filter(
          (cId) => cId in this.columnsById,
        ) || []
      )
    },
    columnsById() {
      return (
        this.modelColumns?.reduce((columnsById, column) => {
          columnsById[column.id] = column
          return columnsById
        }, {}) || {}
      )
    },
    visibleColumns() {
      return this.visibleColumnIds.map((cId) => this.columnsById[cId])
    },
    visibleAttribute() {
      return this.visibleColumns.filter((col) => {
        return col && col.category === 'attribute'
      })
    },
    visibleAttributeIds() {
      return this.visibleAttribute.map((c) => c.id)
    },
    visibleAnnotations() {
      return this.visibleColumns.filter(
        (col) => col && col.category === 'annotation',
      )
    },
    visibleAnnotationsIds() {
      return this.visibleAnnotations.map((c) => c.id)
    },
    searchableAnnotationIds() {
      return this.annotationColumns
        .filter((ann) => ann.isSearchable === true)
        .map((ann) => ann.id)
    },
    searchableColumns() {
      let searchableColumns = this.modelColumns.filter(
        (c) => c.isSearchable === true,
      )
      return this.hasSearchSet && this.searchVisibleOnly
        ? searchableColumns.filter((c) => this.visibleColumnIds.includes(c.id))
        : searchableColumns
    },
    recordsAnnotations() {
      return this.getAnnotationsFromRecords(this.records || [])
    },
    nonVisibleAnnotationsWithValue() {
      return Object.keys(this.recordsAnnotations)
        .filter((id) => !this.visibleAnnotationsIds.includes(id))
        .map((id) => this.recordsAnnotations[id])
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    selectedRecordsAnnotationIds() {
      return Object.keys(this.getAnnotationsFromRecords(this.selectedRecords))
    },
    // selectedRecords() {
    //   const indices = this.selectedIndices
    //   const orderedIndices = indices.sort()
    //   return orderedIndices.map((i) => this.records[i])
    // },
    selectedRecordIds() {
      return this.selectedRecords.map((r) => r.id.value)
    },
    selectedRecordsPermissionsByIds() {
      return this.selectedRecords.reduce((permsById, r) => {
        const recordId = r.id?.value
        const perms = r.permissions?.value?.allowed || []
        permsById[recordId] = perms
        return permsById
      }, {})
    },
    hasChangeAndDeleteOnSelectedRecords() {
      let hasPerms = true
      for (let perms of Object.values(this.selectedRecordsPermissionsByIds)) {
        const canChange = perms.indexOf('change') !== -1
        const canDelete = perms.indexOf('delete') !== -1
        if (!canChange || !canDelete) {
          hasPerms = false
          break
        }
      }
      return hasPerms
    },
    selectedCount() {
      return this.selectedRecords.length
    },
    hasSelectedItems() {
      return this.selectedCount > 0
    },
    startIndex() {
      return this.totalRecords > 0
        ? Math.max(1, this.endIndex - this.pageSize + 1)
        : 0
    },
    endIndex() {
      return Math.min(this.totalRecords, this.currentPage * this.pageSize)
    },
    appliedContext() {
      const isValidContext = (context) =>
        (context?.key && context?.id) ||
        (context?.params?.length > 0 &&
          context.params.some((p) => p.key && (p.id || p.value)))

      if (isValidContext(this.initContext)) {
        return this.initContext
      } else if (isValidContext(this.storedContext)) {
        return this.storedContext
      } else {
        return null
      }
    },
    ownershipOptions() {
      let groupOptions = this.loggedInUser.groups || []
      return [
        { name: 'personal', id: null },
        ...groupOptions,
        { name: 'all', id: null },
      ]
    },
    ownershipFilterUrlParams() {
      let ownershipUrlParam = null
      if (this.ownership?.name === 'personal') {
        ownershipUrlParam = `owner=${this.loggedInUser.username}`
      } else if (this.ownership?.name !== 'all') {
        ownershipUrlParam = `owned_by=${this.ownership?.name}`
      }
      return ownershipUrlParam
    },
    orderingUrlParam() {
      return `ordering=${this.sort?.order === 'desc' ? '-' : ''}${
        this.sort?.columnId
      }`
    },
    currentPageUrlParam() {
      return `page=${this.nextCurrentPage}`
    },
    pageSizeUrlParam() {
      return `page_size=${this.pageSize}`
    },
    contextUrlParam() {
      let params = []

      // Handle new multi-parameter format
      if (this.appliedContext?.params) {
        params = this.appliedContext.params.map(
          (param) => `${param.key}=${param.id || param.value}`,
        )
      }
      // Handle legacy single context format
      else if (this.appliedContext?.key && this.appliedContext?.id) {
        params = [`${this.appliedContext.key}=${this.appliedContext.id}`]
      }

      return params.length ? params.join('&') : null
    },

    modelTypeUrlParam() {
      const modelType = this.modelType.toLowerCase()
      return modelType !== 'total' ? `type=${modelType}` : null
    },
    hasSearchFilter() {
      return this.hasSearchSet || this.isFilteringByAnnotations
    },
    isFilteringByAnnotations() {
      let filtByAnnot = false
      for (const filt of Object.keys(this.filters)) {
        if (this.searchableAnnotationIds.includes(filt)) {
          filtByAnnot = true
          break
        }
      }
      return filtByAnnot
    },
    searchQueryFilters() {
      return this.hasSearchSet
        ? {
            query_operator: 'OR',
            query_expression: this.searchableColumns.map((c) =>
              this.formatQueryFilter(c.id, this.search, 'icontains'),
            ),
          }
        : {}
    },
    columnQueryFilters() {
      const columnQueryFilters = []
      for (const columnId in this.filters) {
        const value = this.filters[columnId]
        let filt
        if (typeof value === 'string') {
          filt = [this.formatStringQueryFilter(columnId, value)]
        } else if (
          typeof value === 'object' &&
          ('start' in value || 'end' in value)
        ) {
          filt = this.formatStartEndObjQueryFilter(columnId, value)
        } else if (typeof value === 'object' && 'option' in value) {
          filt = [this.formatObjectQueryFilter(columnId, value)]
        }
        if (filt) columnQueryFilters.push(...filt)
      }
      return { query_expression: columnQueryFilters }
    },
    queryFilters() {
      return Object.keys(this.searchQueryFilters).length
        ? this.searchQueryFilters
        : this.columnQueryFilters
    },
    queryFiltersURLParams() {
      let queryFiltersURLParams = null
      let queryOperatorParam = null
      let queryParam = null
      if (
        this.queryFilters &&
        this.queryFilters.query_expression &&
        this.queryFilters.query_expression.length
      ) {
        if (this.queryFilters.query_operator) {
          queryOperatorParam = `query_operator=${
            this.queryFilters.query_operator || 'OR'
          }`
        }
        const queryParamValue = this.queryFilters.query_expression.map((q) =>
          encodeURIComponent(JSON.stringify(q)),
        )
        queryParam = `query_expression=[${queryParamValue.join(',')}]`
      }
      queryFiltersURLParams =
        queryParam &&
        [queryOperatorParam, queryParam].filter((p) => p).join('&')
      return queryFiltersURLParams
    },
    fetchUrl() {
      const baseUrl = getModelApiBaseUrl(this.appName, this.modelName)
      const params = [
        this.modelTypeUrlParam,
        'format=json',
        this.contextUrlParam,
        this.ownershipFilterUrlParams,
        this.orderingUrlParam,
        this.currentPageUrlParam,
        this.pageSizeUrlParam,
      ].filter((u) => u)

      return params.length ? `${baseUrl}?${params.join('&')}` : baseUrl
    },
    fetchAnnotationsUrl() {
      const baseUrl = `${this.appName}/${this.modelName}/annotationtypes/`
      const params = [
        this.modelTypeUrlParam,
        'format=json',
        this.contextUrlParam,
        this.ownershipFilterUrlParams,
        'page_size=max',
      ].filter((u) => u)

      return params.length ? `${baseUrl}?${params.join('&')}` : baseUrl
    },
    baseBatchDeleteUrl() {
      return `${this.appName}/${this.modelName}/bulk_delete/`
    },
    /* ANNOTATION */
    selectedRecordsAnnotationsModels() {
      return this.annotations.filter((a) => {
        return this.selectedRecordsAnnotationIds.includes(a.value)
      })
    },
    firstCode() {
      // const r =
      // this.selectedRecords
      let code = ''
      const records =
        this.selectedRecords.length >= 1
          ? this.selectedRecords
          : this.records?.length >= 1
            ? this.records
            : []
      for (let r of records) {
        code = r.code.value
        if (code) break
      }
      return code
    },
    getNextCodeUrl() {
      return this.meta?.view?.detail?.attributes?.find((a) => a.name === 'code')
        ?.widget?.edit?.arguments?.url
    },
    handleRestore() {
      const baseName = 'Restore'
      const name = `${baseName} ${
        this.modelName === 'archives' ? 'archive (1 max)' : 'datasets'
      }`
      const disabled =
        this.selectedCount === 0 ||
        (this.modelName === 'archives' && this.selectedCount > 1)
      const tooltip =
        disabled &&
        'You can only trigger the restoration of archives one by one.'
      return {
        tooltip,
        name,
        disabled,
        function: this.toggleRestoreModal,
      }
    },

    // --- Tooltip computed properties ---
    // Cached to avoid creating new object references on every render,
    // which triggers infinite reactivity loops in floating-vue v5.
    BATCH_CREATE_TOOLTIP: () => BATCH_CREATE_TOOLTIP,
    BATCH_EDIT_TOOLTIP: () => BATCH_EDIT_TOOLTIP,
    mergeSamplesTooltip() {
      return [
        'Merge samples:',
        this.selectedRecords.length <= 1
          ? '<p>Please select more than one sample. </p>'
          : undefined,
        !this.hasChangeAndDeleteOnSelectedRecords
          ? '<p>You lack privileges on one or some items to perform a merge. Change permission is needed on kept sample. Delete permission is needed on discarded samples. </p>'
          : undefined,
      ]
        .filter((v) => v)
        .join(' ')
    },
    ownershipTooltip() {
      return `<p>Ownership (permission) filter on items.</p>
        <p>Currently viewing <code>${capitalizeWords(
          this.ownership?.name || '',
        )}</code> items.</p>`
    },
    ownershipIcon() {
      const name = (this.ownership?.name || '').toLowerCase()
      if (name === 'personal') return 'fa-solid fa-user'
      if (name === 'all') return 'fa-solid fa-globe'
      return 'fa-solid fa-users'
    },
    ownershipLabel() {
      const name = (this.ownership?.name || '').toLowerCase()
      if (name === 'personal') return 'My items'
      return capitalizeWords(this.ownership?.name || '')
    },
    ownershipDesktopTooltip() {
      return `<p class='pb-2'>
        Ownership (permission) filter on items. Currently
        viewing <code>${this.ownershipLabel}</code> items.
      </p>
      <ul>
        <li><code>My items</code> filters items belonging to you,</li>
        <li><code>Group</code> filters items belonging to a group</li>
        <li><code>All</code> displays all items</li>
      </ul>`
    },
    annotationsTooltip() {
      return `Show annotations found on records:
        <ul>${this.nonVisibleAnnotationsWithValue
          .map(({ name }) => `<li><code>${name}</code></li>`)
          .join('')}</ul>`
    },
  },
  methods: {
    pluralize,
    showSelectionHint() {
      const el = this.$refs.selectedTag
      if (!el) return
      this.destroySelectionHint()
      this._selectionHintTippy = tippy(el, {
        content: SELECTION_HINT_HTML,
        allowHTML: true,
        placement: 'top',
        theme: 'selection-hint-tooltip',
        trigger: 'manual',
        interactive: true,
        appendTo: () => document.body,
        arrow: true,
        offset: [0, 8],
        onClickOutside: () => this.destroySelectionHint(),
      })
      this._selectionHintTippy.show()
    },
    toggleSelectionHint() {
      if (this._selectionHintTippy?.state?.isVisible) {
        this.destroySelectionHint()
      } else {
        this.showSelectionHint()
      }
    },
    destroySelectionHint() {
      if (this._selectionHintTippy) {
        const instance = this._selectionHintTippy
        this._selectionHintTippy = null
        instance.hide()
        // Destroy after hide completes to avoid tippy's internal
        // hide() call on an already-destroyed instance.
        requestAnimationFrame(() => instance.destroy())
      }
    },
    ...mapMutations({
      setSearchVisibleOnly(commit, searchVisOnly) {
        commit(
          `${this.sessionSettingsPathPrefix}/SET_SEARCH_VISIBLE_ONLY`,
          searchVisOnly,
        )
      },
      setShowFilters(commit, showFilters) {
        commit('settings/SET_SHOW_FILTERS', showFilters)
      },
      toggleShowFilters(commit) {
        commit('settings/TOGGLE_SHOW_FILTERS')
      },
      async setContext(commit, context) {
        if (context) {
          commit(`${this.sessionSettingsPathPrefix}/SET_CONTEXT`, context)
        } else {
          commit(`${this.sessionSettingsPathPrefix}/UNSET_CONTEXT`)
        }
        this.nextCurrentPage = 1
        await this.$nextTick()
        this.loadData()
      },
      moveColumns(commit, { fromIndices, toIndex }) {
        return commit(`${this.sessionSettingsPathPrefix}/MOVE_COLUMN`, {
          fromIndices,
          toIndex,
        })
      },
    }),
    ...mapActions({
      async initSessionSettings(dispatch, settings) {
        // columnIds
        return dispatch(
          `${this.sessionSettingsPathPrefix}/initSessionSettings`,
          settings,
        )
      },
      async initServerSettings(dispatch) {
        return dispatch(`${this.serverSettingsPathPrefix}/initSettings`, '')
      },
      async resetToDefaultSettings(dispatch) {
        return dispatch(
          `${this.sessionSettingsPathPrefix}/resetToDefaultSettings`,
          {
            defaultVisibleColumnIds: this.defaultVisibleColumnIds,
            defaultFilters: this.defaultFilters,
          },
        )
      },
      async resetToServerSettings(dispatch) {
        return dispatch(
          `${this.sessionSettingsPathPrefix}/resetToServerSettings`,
          {
            defaultVisibleColumnIds: this.defaultVisibleColumnIds,
            defaultFilters: this.defaultFilters,
          },
        )
      },
      changeOwnership(dispatch, ownership) {
        return dispatch(
          `${this.sessionSettingsPathPrefix}/changeOwnership`,
          ownership,
        )
      },
      changeSort(dispatch, sort) {
        return dispatch(`${this.sessionSettingsPathPrefix}/changeSort`, sort)
      },
      changePageSize(dispatch, pageSize) {
        return dispatch(
          `${this.sessionSettingsPathPrefix}/changePageSize`,
          pageSize,
        )
      },
      changeSearch(dispatch, search) {
        return dispatch(
          `${this.sessionSettingsPathPrefix}/changeSearch`,
          search,
        )
      },
      addSessionFilter(dispatch, { id, value }) {
        return dispatch(`${this.sessionSettingsPathPrefix}/addFilter`, {
          id,
          value,
        })
      },
      removeSessionFilter(dispatch, id) {
        return dispatch(`${this.sessionSettingsPathPrefix}/removeFilter`, id)
      },
      clearSessionFilters(dispatch) {
        return dispatch(`${this.sessionSettingsPathPrefix}/clearFilters`)
      },
      toggleColumnById(dispatch, { columnId, index }) {
        return dispatch(`${this.sessionSettingsPathPrefix}/toggleColumnById`, {
          id: columnId,
          index,
        })
      },
      showColumns(dispatch, { ids, toIndex }) {
        return dispatch(`${this.sessionSettingsPathPrefix}/showColumns`, {
          ids,
          index: toIndex,
        })
      },
      removeColumnByIndex(dispatch, { index }) {
        return dispatch(
          `${this.sessionSettingsPathPrefix}/removeColumnByIndex`,
          { index },
        )
      },
      removeColumnsByIndices(dispatch, { indices }) {
        return dispatch(
          `${this.sessionSettingsPathPrefix}/removeColumnsByIndices`,
          { indices },
        )
      },
      handleToggleAllColumns(dispatch, { ids, show }) {
        return dispatch(`${this.sessionSettingsPathPrefix}/toggleColumns`, {
          ids,
          show,
        })
      },
    }),
    /* Table columns */
    async handleToggleColumnById(columnId, index) {
      await this.toggleColumnById({ columnId, index })
    },
    /* Table filters */
    setFilter(columnId, value) {
      if (!value || value === '') {
        this.removeFilter(columnId)
      } else {
        this.addFilter(columnId, value)
      }
    },
    addFilter(columnId, value) {
      this.addSessionFilter({ id: columnId, value })
      this.nextCurrentPage = 1
      this.loadData()
    },
    removeFilter(columnId) {
      this.removeSessionFilter(columnId)
      this.nextCurrentPage = 1
      this.loadData()
    },
    clearFilters() {
      this.clearSessionFilters()
      this.nextCurrentPage = 1
      this.loadData()
    },
    formatQueryFilter(columnId, query, operator) {
      return {
        category: columnId,
        operator,
        value: query,
      }
    },
    formatStringQueryFilter(columnId, value) {
      return this.formatQueryFilter(columnId, value, 'icontains')
    },
    formatStartEndObjQueryFilter(columnId, value) {
      const filt = []
      if (value.start) {
        const startFilt = this.formatQueryFilter(columnId, value.start, '>=')
        filt.push(startFilt)
      }
      if (value.end) {
        const endFilt = this.formatQueryFilter(columnId, value.end, '<=')
        filt.push(endFilt)
      }
      return filt
    },
    formatObjectQueryFilter(columnId, { option }) {
      // FIXME need to encode all filter objects differently
      // e.g. { field: "owner", value: { option: }}
      // to get choices.value_field from meta
      const { username, id, value } = option
      const query = Array.isArray(option) ? option[0] : username || value || id
      return this.formatQueryFilter(columnId, query, 'iexact')
    },
    isCurrentLoadRequest(requestId, abortCtlr) {
      return (
        !abortCtlr.signal.aborted &&
        this.abortCtlr === abortCtlr &&
        this.activeLoadRequestId === requestId
      )
    },
    async fetchData(url, signal = null) {
      const config = signal ? { signal } : {}
      return this.hasSearchFilter
        ? put(url, { data: this.queryFilters, ...config })
        : get(
            [url, this.queryFiltersURLParams].filter((p) => p).join('&'),
            config,
          )
    },
    processRawRecords(records) {
      return records.map((r) => ({
        ...r,
        __meta__: { selected: false, ...getFileFlags(r) },
      }))
    },
    async loadData() {
      const isFirstLoad = this.records === null
      const requestId = this.activeLoadRequestId + 1
      const abortCtlr = new AbortController()

      this.activeLoadRequestId = requestId
      this.isLoadingRecords = isFirstLoad
      this.isRefreshing = !isFirstLoad
      this.selectedRecords = []
      this.relevantAnnotations = null

      // Cancel any previous request (race condition)
      if (this.abortCtlr) this.abortCtlr.abort()
      // Create new AbortController for this loadData call
      this.abortCtlr = abortCtlr

      try {
        this.currentPage = this.nextCurrentPage
        const response = await this.fetchData(this.fetchUrl, abortCtlr.signal)

        if (!this.isCurrentLoadRequest(requestId, abortCtlr)) return

        if (response.status === 200) {
          const rawRecords = response.data.results || []
          const records = this.processRawRecords(rawRecords)
          this.records = Object.freeze(records)
          this.totalRecords = response.data.total
          this.totalPages = response.data.total_pages
          this.hasLoadedOnce = true
        }
      } catch (error) {
        // Don't show error for aborted requests
        if (error.name === 'AbortError' || abortCtlr.signal.aborted) return
        if (!this.isCurrentLoadRequest(requestId, abortCtlr)) return

        this.nextCurrentPage = 1
        this.currentPage = this.nextCurrentPage
        const errorMessage =
          error.response?.data?.message || error.message || 'error'
        this.$store.commit('notify', {
          type: 'error',
          message: `${errorMessage} (${error.response?.statusText || ''}).`,
        })
        this.hasLoadedOnce = true
      } finally {
        if (this.isCurrentLoadRequest(requestId, abortCtlr)) {
          this.lastSearch = this.search
          this.isLoadingRecords = false
          this.isRefreshing = false
          if (this.abortCtlr === abortCtlr) {
            this.abortCtlr = null
          }
        }
      }
    },
    async handleDeleteSelected() {
      if (this.selectedCount) {
        try {
          const response = await del(this.baseBatchDeleteUrl, {
            data: { ids: this.selectedRecordIds },
          })
          if (
            response.status === 200 ||
            response.status === 202 ||
            response.status === 204
          ) {
            this.$store.commit('notify', {
              type: 'success',
              message: response.data.message,
              duration: 5000,
            })
            this.loadData()
          }
        } catch (e) {
          const message =
            (e.response && e.response.data && e.response.data.message) ||
            e.message ||
            'Cannot delete selected items'

          this.$store.commit('notify', {
            type: 'error',
            message: message,
          })
        }
      }
    },
    async handleRemoveDatafilecopies() {
      const path = 'data_management/datasets/remove_datafilecopies/'

      this.isRemovingDatafilecopies = true
      try {
        const response = await post(path, {
          data: {
            ids: [this.initContext.id],
            datafilecopies: this.selectedRecordIds,
          },
        })
        if (response.status >= 200 && response.status <= 300) {
          this.$store.commit('notify', {
            message: response.data.message,
            type: 'success',
            duration: 10000,
          })
        }
      } catch (e) {
        const message =
          (e.response && e.response.data && e.response.data.message) ||
          e.message ||
          'Cannot remove the datafilecopies!'

        this.$store.commit('notify', {
          type: 'error',
          message: message,
        })
      }
      this.toggleBatchRemoveModal()
      this.isRemovingDatafilecopies = false
    },
    handleGetNextCode() {
      this.isGetNextCodeModalOpened = !this.isGetNextCodeModalOpened
    },
    handleClickColumnSettings() {
      this.rightOverlayOpen = !this.rightOverlayOpen
    },
    async handleSaveSettings() {
      /* Currently, it is needed to retrieve the raw settings object and
       * replace needed key before resubmitting it. Later, it might be better
       * to POST to specific endpoints */
      /* Also: Currently, we overwrite the whole list setting value with the
       * settings object, this allow to remove keys not associated with a
       * value. Later, it could become more precise */
      if (Object.keys(this.settingsToSave).length) {
        const settingsSaveIdString = [
          `<tt>${this.modelName}</tt>`,
          this.modelType !== 'DEFAULT' ? ` - <tt>${this.modelType}</tt>` : '',
        ].join('')

        const allSettings = cloneDeep(this.allSettings)
        set(
          allSettings,
          [
            this.vuexPrefix,
            this.appName,
            this.modelName,
            this.modelType,
            'list',
          ]
            .filter((el) => el !== '') // will remove empty vuex prefix
            .join('.')
            .toLowerCase(),
          this.settingsToSave,
        )
        try {
          await this.$store.dispatch('settings/saveSettings', {
            username: this.loggedInUser.username,
            settings: allSettings,
          })
          await this.initServerSettings()
          this.initSessionSettings(this.initSettings).then(() => {
            this.$store.commit('notify', {
              type: 'success',
              message: `List settings for ${settingsSaveIdString}saved successfully`,
            })
            this.isOpenEllipsisMenu = false
          })
        } catch (e) {
          this.$store.commit('notify', {
            type: 'error',
            message: `Error saving ${settingsSaveIdString} settings. Error: ${e}`,
          })
        }
      }
    },
    async loadServerSettings() {
      await this.resetToServerSettings()
      this.isOpenEllipsisMenu = false
      this.loadData()
    },
    async loadDefaultSettings() {
      await this.resetToDefaultSettings()
      this.isOpenEllipsisMenu = false
      this.loadData()
    },
    getAnnotationsFromRecords(records) {
      return (
        records?.reduce((annots, r) => {
          return {
            ...annots,
            ...Object.keys(r)
              .filter((k) => matchUuid(k))
              .reduce((all, id) => ({ ...all, [id]: r[id] }), {}),
          }
        }, {}) || {}
      )
    },
    async fetchAnnotations() {
      this.relevantAnnotations = null
      this.isFetchingAnnotations = true
      try {
        const response = await this.fetchData(this.fetchAnnotationsUrl)
        if (
          response.status === 200 ||
          response.status === 202 ||
          response.status === 204
        ) {
          const annotIds =
            (response.data.results &&
              response.data.results.map((a) => a.name.value.id)) ||
            []
          this.relevantAnnotations = this.annotations.filter((a) =>
            annotIds.includes(a.value),
          )
        }
      } catch (e) {
        console.error('Could not fetch annotations', e)
      } finally {
        this.isFetchingAnnotations = false
      }
    },
    async setSearch(search) {
      this.nextCurrentPage = 1
      await this.changeSearch(search)
    },
    debounceSetSearch: debounce(async function (search) {
      await this.setSearch(search)
    }, 250),
    doSearch: debounce(async function () {
      this.loadData()
    }, 100),
    async clearSearch() {
      await this.setSearch('')
      this.doSearch()
    },
    setPage: function (newPage) {
      if (
        newPage !== this.nextCurrentPage &&
        newPage > 0 &&
        newPage <= this.totalPages
      ) {
        this.nextCurrentPage = newPage
      }
    },
    changePage(pageNumber) {
      this.setPage(pageNumber)
      this.loadData()
    },
    handleEdit() {
      this.batchEditSuccess = false
      this.isOpenBatchEditModal = true
    },
    async handleArchive() {
      this.toggleArchiveModal()
      await this.getArchivabaleDatafiles()
    },
    toggleArchiveModal() {
      this.isArchiveModalOpened = !this.isArchiveModalOpened
    },
    async getArchivabaleDatafiles() {
      let path = `data_management/datasets/archivable_datafilecopies/`
      const payload = this.getFiltersForFetchingDatafiles()
      this.archivableDatafiles = null

      post(path, { data: payload })
        .then((res) => {
          this.archivableDatafiles = res.data
        })
        .catch((error) => {
          console.error(error)
          this.toggleArchiveModal()
          this.$store.commit('notify', {
            type: 'error',
            message:
              error.response.data.message ||
              'Error fetching archivable datafiles!',
          })
        })
    },
    getFiltersForFetchingDatafiles() {
      const FILTER_PARAM = {
        projects: 'project_ids',
        studies: 'study_ids',
        datasets: 'dataset_ids',
        assays: 'assay_ids',
      }
      return {
        [FILTER_PARAM[this.modelName]]: this.selectedRecordIds,
      }
    },
    editOrderStatus() {
      const items = this.selectedRecords
      if (items.length === 0 || this.batchOrderStatus === '') {
        return
      }
      const path = 'stocks/ordereditems/bulk_update/'
      const payload = items.map((item) => ({
        results: {
          id: {
            value: item.id.value,
          },
          status: {
            value: this.batchOrderStatus,
          },
        },
      }))
      patch(path, { data: payload })
        .then((response) => {
          if (response.status === 200) {
            this.loadData()
          }
        })
        .catch((error) => {
          let errorMessage
          if (error.response) {
            errorMessage = error.response.data.message
          } else {
            errorMessage = error.message
          }
          this.$store.commit('notify', {
            type: 'error',
            message: errorMessage,
          })
        })
    },
    handleAddToOrder() {
      const orderedItems = []
      for (const record of this.selectedRecords) {
        const item = cloneDeep(this.orderedItemTemplate)
        const attrTransferList = [
          'batch_number',
          'catalogue_number',
          'currency',
          'description',
          'name',
          'price',
          'stock',
          'stock_type',
          'vendor',
        ]
        for (const attr in item) {
          if (attr === 'stock' && this.modelName === 'ordereditems') {
            item.stock.value = record.stock.value
          }
          if (!attrTransferList.includes(attr)) {
            continue
          }
          const slot = item[attr]
          if (slot && record[attr]) {
            slot.value = record[attr].value
            if (attr === 'name' && this.modelName === 'ordereditems') {
              slot.value.id = null
            }
          }
        }
        if (this.modelName !== 'ordereditems') {
          const slot = item.stock
          slot.value = {
            id: record.id.value,
            model_type: mapModelType(this.modelType),
            model_name: modelNameSingularize(this.modelName),
            app_name: 'stocks',
            // TODO get `name` prop from meta
            name: item.name.value.name,
          }
        }
        item._tempId = {
          name: '_tempId',
          value: uuid(),
        }
        item._validations = {}
        orderedItems.push(item)
      }
      this.$store.dispatch('orders/addOrderedItems', orderedItems)
    },
    toggleDatasetCopyModal() {
      this.isDatasetCopyModalOpened = !this.isDatasetCopyModalOpened
    },
    toggleBatchDeleteModal() {
      this.isOpenBatchDeleteModal = !this.isOpenBatchDeleteModal
    },
    toggleBatchRemoveModal() {
      this.isOpenBatchRemoveModal = !this.isOpenBatchRemoveModal
    },
    toggleBatchUnlinkModal() {
      this.isOpenBatchUnlinkModal = !this.isOpenBatchUnlinkModal
    },
    toggleBatchEditOrderStatusModal() {
      this.isOpenChangeOrderStatusModal = !this.isOpenChangeOrderStatusModal
    },
    cleanupLegacyColumns() {
      /* Remove legacy columns from session i.e. columns that might have been
       * existing on a model and as so saved in settings but no more relevant
       * for the given model. It e.g. happened for the "autogenerated" column */
      const ind =
        this.sessionVisibleColumnIds?.reduce((indices, id, index) => {
          const knownModel = id in this.columnsById || false
          if (!knownModel) indices.push(index)
          return indices
        }, []) || []
      if (ind.length > 0) this.removeColumnsByIndices({ indices: ind })
    },
    toggleDeleteDatasetsModal() {
      this.isDeleteDatasetsModalOpened = !this.isDeleteDatasetsModalOpened
    },
    setDatasetsDeletionButtonStatus({ disabled, reason }) {
      this.datasetsDeletionButtonStatus = { disabled, reason }
    },
    triggerDatasetsRemoval() {
      this.isDeletingDatasets = true
      this.$refs.deleteDatasets.handleDatasetsDeletion()
    },
    handleDatasetsDeletionDone(deleted) {
      this.isDeletingDatasets = false
      if (deleted) this.toggleDeleteDatasetsModal()
    },
  },
  watch: {
    initialised: {
      immediate: true,
      handler: async function (init) {
        if (init) {
          this.$emit('init', this.loadData)

          let initOwnership
          if (this.initOwnership) {
            const ownKey = ['all', 'personal'].includes(this.initOwnership)
              ? 'name'
              : 'id'
            initOwnership = this.ownershipOptions.find(
              (o) => o[ownKey] === this.initOwnership,
            )
          }

          await this.initSessionSettings({
            ...this.initSettings,
            ...(this.initFiltersSetting && {
              initFilters: this.initFiltersSetting,
            }),
            ...(initOwnership && { initOwnership }),
          })
          await this.cleanupLegacyColumns()
          this.showFilters =
            Object.keys(this.filters).length > 0 && this.vuexPrefix === ''
          this.loadData()
        }
      },
    },
    selectedRecords: function (selected) {
      this.$emit('selected', selected)
    },
    selectedCount(count, oldCount) {
      if (count === 1 && oldCount === 0 && this.selectionMode !== 'radio') {
        if (!this.selectionHintShownOnce) {
          this.selectionHintShownOnce = true
          this.$nextTick(() => this.showSelectionHint())
        }
      } else if (count !== 1) {
        this.destroySelectionHint()
      }
    },
    isLoading: function (newVal) {
      this.$emit('loading', newVal)
    },
    isLoadingRecords: function (newVal) {
      if (this.containerState && this.containerState.loading !== newVal) {
        this.containerState.loading = newVal
      }
    },
    isRefreshing: function (newVal) {
      if (this.containerState) {
        this.containerState.isRefreshing = newVal
      }
    },
    error: function (newVal) {
      this.$emit('error-state', newVal)
    },
    isOpenExportModal2(opened) {
      if (opened && this.allowsAnnotations && !this.relevantAnnotations) {
        this.fetchAnnotations()
      }
    },
    isOpenExportModal3(opened) {
      if (opened && this.allowsAnnotations && !this.relevantAnnotations) {
        this.fetchAnnotations()
      }
    },
    isOpenBatchEditModal(opened) {
      if (opened && this.allowsAnnotations && !this.relevantAnnotations) {
        this.fetchAnnotations()
      }
    },
  },
  mounted() {
    this.isLoading = false
    if (this.containerState) {
      this.containerState.refresh = this.loadData
    }
  },
  beforeUnmount() {
    if (this.containerState) {
      this.containerState.refresh = null
      this.containerState.isRefreshing = false
    }
    this.destroySelectionHint()
    this.activeLoadRequestId += 1
    // Cancel any pending requests when component is destroyed
    if (this.abortCtlr) {
      this.abortCtlr.abort()
    }
  },
}
</script>

<style lang="scss" scoped>
.list-container {
  display: flex;
  flex-direction: column;
  overflow: hidden;

  max-height: 100%;
  > .top-controls {
    flex-shrink: 0;
  }
}

.list-container.is-compact-list {
  .data-sumup {
    :deep(.loader-wrp) {
      width: fit-content;
      height: fit-content;
    }
  }
}

.top-controls {
  background-color: var(--bg-base);
  padding: 0.5rem 0.7rem;
  width: 100%;
  max-width: 100%;
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  .control-grid {
    display: grid;
    gap: 0.35rem 0.5rem;
    align-items: center;
    grid-template-columns: minmax(0, auto) minmax(0, 1fr) minmax(0, auto);
  }

  // ── Row 1: create | search | ownership ──
  .top-row {
    grid-template-areas: 'create search ownership';

    .cell-create {
      grid-area: create;
      display: flex;
      align-items: center;
    }
    .cell-search {
      grid-area: search;
      min-width: 0;
    }
    .cell-ownership {
      grid-area: ownership;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      flex-shrink: 0;
      gap: var(--space-2);
    }

    .global-search {
      display: flex;
      align-items: stretch;
      gap: 0;
      min-width: 0;

      > .control.is-expanded {
        flex: 1 1 0;
        min-width: 0;
      }

      .search-input-wrap {
        position: relative;
        height: 100%;

        input {
          width: 100%;
          height: 100%;
        }
      }

      .search-filter-badge {
        position: absolute;
        right: 0.4rem;
        top: 50%;
        transform: translateY(-50%);
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 0.15rem;
      }

      // Join cog button, input, and search button visually as an addon group
      :deep(.button) {
        border-radius: 0;
      }
      > .control:first-child :deep(.button) {
        border-start-start-radius: var(--bulma-radius-small, 4px);
        border-end-start-radius: var(--bulma-radius-small, 4px);
      }
      > .control:last-child :deep(.button) {
        border-start-end-radius: var(--bulma-radius-small, 4px);
        border-end-end-radius: var(--bulma-radius-small, 4px);
      }
      .search-input-wrap input {
        border-radius: 0;
      }
    }
  }

  // ── Row 2: [left] pagination + selected  [right] quick actions ──
  // flex-shrink: 0 on bottom-right forces block-level wrap before icon wrap
  .bottom-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem 0.5rem;
    font-size: 0.85rem;

    .bottom-left {
      flex: 0 0 max-content;
      max-width: 100%;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.25rem;

      .selected-count {
        background-color: var(--bg-selected);
        font-family: var(--font-table);
        font-weight: var(--font-bold);
        white-space: nowrap;
      }
    }

    .bottom-right {
      display: flex;
      flex: 1 0 max-content;
      max-width: 100%;
      justify-content: flex-end;
    }
  }

  .list-quick-actions {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 0;

    .quick-action-group {
      display: flex;
      align-items: center;
      gap: 0;

      .sep.icon {
        pointer-events: none;
        font-size: 0.15rem;
        opacity: 0.35;
        padding: 0 0.25rem;
      }

      .gp-content:empty + .sep.icon {
        display: none;
      }
    }

    .filter-split {
      display: inline-flex;
      align-items: center;
      gap: 0;
    }

    .filter-toggle-btn {
      display: inline-flex;
      align-items: center;
      gap: 0;
      background: none;
      border: none;
      padding: 0.1em;
      cursor: pointer;
      border-radius: 4px;
      color: var(--text-light, hsl(0, 0%, 48%));
      font-size: 1em;
      transition:
        color 0.15s,
        background-color 0.15s;

      .icon:has(+ .icon-eye) {
        margin-right: -0.5rem;
      }

      &:hover,
      &:focus-visible {
        background-color: var(--scheme-main-bis, hsl(0, 0%, 96%));
        color: var(--primary, hsl(209, 62%, 50%));
      }

      &.is-active {
        color: var(--primary, hsl(209, 62%, 50%));
      }

      .icon-eye {
        font-size: 0.6em;
        opacity: 0.55;
        margin-left: -0.2em;
      }

      &.is-active .icon-eye {
        opacity: 0.8;
      }
    }
  }

  @container (max-width: #{$cq-md-lg}) {
    .list-quick-actions {
      flex-wrap: wrap-reverse;
    }
  }

  @container (max-width: #{$cq-md}) {
    .top-row {
      grid-template-columns: auto auto;
      grid-template-areas:
        'create    ownership'
        'search    search';
    }
  }
}

.data-table {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.cursor-pointer {
  cursor: pointer;
}

button.button.button-ellipsis,
button.button.button-ellipsis span {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ownership-dropdown.buttons.has-addons button {
  margin-bottom: 0;
}
</style>
